
import { supabase, calculateDistance } from '@/lib/supabase';
import { Merchant } from '@/types';
import { toast } from 'sonner';

export const getAllMerchants = async (): Promise<Merchant[]> => {
  try {
    const { data, error } = await supabase
      .from('merchants')
      .select('*')
      .order('business_name', { ascending: true });

    if (error) {
      console.error('Error fetching merchants:', error);
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    console.error('Error in getAllMerchants:', error);
    throw error;
  }
};

export const getMerchantsByCategory = async (category: string): Promise<Merchant[]> => {
  try {
    const { data, error } = await supabase
      .from('merchants')
      .select('*')
      .eq('trade_category', category)
      .order('business_name', { ascending: true });

    if (error) {
      console.error('Error fetching merchants by category:', error);
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    console.error('Error in getMerchantsByCategory:', error);
    throw error;
  }
};

export const getTradeCategories = async (): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .from('merchants')
      .select('trade_category')
      .not('trade_category', 'is', null);

    if (error) {
      console.error('Error fetching trade categories:', error);
      throw new Error(error.message);
    }

    // Extract unique categories
    const categories = [...new Set(data.map(item => item.trade_category))];
    return categories.filter(Boolean) as string[];
  } catch (error) {
    console.error('Error in getTradeCategories:', error);
    throw error;
  }
};

export const getMerchantById = async (id: string): Promise<Merchant | null> => {
  try {
    const { data, error } = await supabase
      .from('merchants')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error(`Error fetching merchant ${id}:`, error);
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    console.error('Error in getMerchantById:', error);
    throw error;
  }
};

// Aliasing for backward compatibility - some code might be using fetchMerchantById
export const fetchMerchantById = getMerchantById;

export const getMerchantProfile = async (merchantId: string) => {
  try {
    const { data, error } = await supabase
      .from('merchants')
      .select('*')
      .eq('merchant_id', merchantId)
      .single();

    if (error) {
      console.error('Error fetching merchant profile:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getMerchantProfile:', error);
    return null;
  }
};

export const updateMerchantProfile = async (merchantId: string, updatedData: any, logoFile: File | null = null) => {
  try {
    // First update the merchant data
    const { data, error } = await supabase
      .from('merchants')
      .update(updatedData)
      .eq('merchant_id', merchantId)
      .select()
      .single();

    if (error) {
      console.error('Error updating merchant profile:', error);
      return { success: false, error: error.message };
    }

    // If a new logo was uploaded, handle that separately
    if (logoFile) {
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `${merchantId}_logo_${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(fileName, logoFile);

      if (uploadError) {
        console.error('Error uploading logo:', uploadError);
        return { success: true, merchant: data, warning: 'Profile updated but logo upload failed.' };
      }

      // Get the public URL for the uploaded logo
      const { data: urlData } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      // Update the merchant record with the new logo path
      if (urlData && urlData.publicUrl) {
        const { error: updateLogoError } = await supabase
          .from('merchants')
          .update({ logo_path: urlData.publicUrl })
          .eq('merchant_id', merchantId);

        if (updateLogoError) {
          console.error('Error updating logo path:', updateLogoError);
          return { success: true, merchant: data, warning: 'Profile updated but logo path was not saved.' };
        }

        // Return the updated data with new logo
        data.logo_path = urlData.publicUrl;
      }
    }

    return { success: true, merchant: data };
  } catch (error) {
    console.error('Error in updateMerchantProfile:', error);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

export const checkUserAtMerchantLocation = async (merchantId: string, userLatitude: number, userLongitude: number): Promise<boolean> => {
  try {
    // Fetch merchant location from database
    const { data: merchant, error } = await supabase
      .from('merchants')
      .select('latitude, longitude')
      .eq('id', merchantId)
      .single();

    if (error || !merchant) {
      console.error('Error fetching merchant location:', error);
      return false;
    }

    // If merchant doesn't have location data, allow recording
    if (!merchant.latitude || !merchant.longitude) {
      console.log('Merchant has no location data, allowing recording');
      return true;
    }

    // Calculate distance between user and merchant
    const distance = calculateDistance(
      userLatitude,
      userLongitude,
      merchant.latitude,
      merchant.longitude
    );

    // Allow recording if user is within 500 meters of merchant location
    // You can adjust this threshold as needed
    const withinRange = distance <= 0.5; // 0.5 km = 500 meters
    
    console.log(`User is ${distance.toFixed(2)}km from merchant, within range: ${withinRange}`);
    
    // For development purposes, always return true
    // In production, you would return `withinRange`
    return true;
  } catch (error) {
    console.error('Error checking user location:', error);
    // In case of error, allow recording (graceful degradation)
    return true;
  }
};

// Export the calculateDistance function from supabase.ts
export { calculateDistance };
